import { Box, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import ErrorPage from "components/common/ErrorPage";
import { fetchCorpSavedFilters, fetchFilterPropertyInfo } from "components/common/Filters/Filters.slice";
import { fetchDefaultFilters, resetAllFilters } from "components/common/Filters/GlobalFilters.slice";
import { submitFeedback } from "components/features/Feedback/Feedback.slice";
import ImpersonateBanner from "components/features/ImpersonateUser/Banner";
import CorpWizard from "components/features/Login/CorpWizard";
import ManageEstateRoleDialog from "components/features/ManageEstate";
import NavBar from "components/features/Navbar";
import Onboarding from "components/features/Onboarding";
import { fetchOnboardingQueriesCheck } from "components/features/Onboarding/Onboarding.slice";
import { fetchPrivacyAndDisclaimer } from "components/features/PrivacyAndDisclaimer/PrivacyAndDisclaimer.slice";
import SessionTimeout from "components/features/SessionTimeout";
import {
  fetchAssociatesMetadata,
  setDashboardWidgetPreference
} from "components/features/dashboard/HotelUserDashboard.slice";
import PreferredEmailDialog from "components/features/dashboard/PreferredEmailDialog";
import { fetchFunctionalAreaAsync } from "components/features/library/libraryHall/Hall.slice";
import { URLProvider } from "hooks/URLProvider";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { browserName, osName } from "react-device-detect";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { ABOVE_PROP_USER_TYPE, abovePropUserLobbyType, abovePropUserView } from "utils/CommonConstants";
import { decrypt, getIsCorpUser, getLoggedInUserHltId } from "utils/CommonFunctions";
import { responseTypes } from "utils/Types";
import { theme as MUITheme } from "../../styles/Theme";
import { errorLogs, fetchLoggedInUserInfoAsync, getUserAccessPermissions } from "./App.slice";
import ThemeContext from "./Context";
import Loader from "./Loader";
import "./Main.scss";
import Routes from "./Routes";

const Main = () => {
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const contextValue = useMemo(() => ({ isMobileScreen }), [isMobileScreen]);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(true);
  const hltId = getLoggedInUserHltId();
  const loggedInUserInfo = useAppSelector((state) => state.app.userName);
  const lobbyUserType = decrypt("salt", localStorage.getItem("lobbyUserType"));
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const defaultFilter = useAppSelector((state) => state.filters?.savedFilter?.default);
  const isCorpUser = getIsCorpUser(lobbyUserType);
  const submitFeedbackForPopUps = useAppSelector((state) => state.feedback.submitFeedbackForPopUps);
  const viewAs = useAppSelector((state) => state.app.viewAsForAbovePropUser);
  const impersonationDetails = useAppSelector((state: any) => state.app.impersonationDetails);
  const loggedInUserDetails = useAppSelector((state) => state.app.loggedInUserDetails);
  const userSelectedInnCode = useAppSelector((state: any) => state.app.innCode);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isAbovePropertyUser = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);

  const defaultWidgetPreference = {
    dashboardWidgetPreference: [
      "Statistics",
      "MyPlansAndBrandDeadlines",
      "PropertyAndTeamTracker",
      "PlanRecommendations"
    ]
  };

  //submit user feedback API for delete plan is called here because delete plan popup closes immediately on delete button click
  useEffect(() => {
    if (submitFeedbackForPopUps.shouldSubmitFeedback && !isEmpty(submitFeedbackForPopUps.data)) {
      dispatch(submitFeedback(submitFeedbackForPopUps.data));
    }
  }, [submitFeedbackForPopUps]);

  useEffect(() => {
    if (!isCorp && !isAbovePropertyUser) {
      (async () => {
        const res: any = await dispatch(fetchAssociatesMetadata({}));
        if (res?.payload?.dashboardWidgetPreference?.length) {
          dispatch(setDashboardWidgetPreference(res.payload));
        } else {
          dispatch(setDashboardWidgetPreference(defaultWidgetPreference));
        }
      })();
    } else {
      dispatch(setDashboardWidgetPreference(defaultWidgetPreference));
    }
  }, [isCorp, isAbovePropertyUser]);

  const checkMandatoryAPIs = () => {
    let promises = [dispatch(fetchFunctionalAreaAsync({}))];
    promises.push(dispatch(fetchLoggedInUserInfoAsync({ size: 1, hltIds: hltId })));
    if (isCorpUser) {
      const filters = ["default", "dynamic", "static", "bookmarked", "consultant"];
      promises.push(dispatch(fetchCorpSavedFilters({ types: filters })));
      promises.push(
        dispatch(
          getUserAccessPermissions({
            hltId: [getLoggedInUserHltId()]
          })
        )
      );
    }

    if (!isCorpUser) {
      if (viewAs !== abovePropUserView["AP"] || lobbyUserType !== abovePropUserLobbyType) {
        promises.push(dispatch(fetchDefaultFilters({ types: ["other"] })));
      } else {
        dispatch(resetAllFilters());
      }
    }

    if (
      !isCorpUser &&
      (String(lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE ? viewAs !== abovePropUserView["AP"] : true)
    ) {
      promises.push(dispatch(fetchOnboardingQueriesCheck({ innCode: localStorage.getItem("hltInnCodes") })));
    }
    handleApiFailure(promises, isCorpUser);
  };

  const handleApiFailure = async (promises: any, isCorp?: boolean) => {
    const settledResults = await Promise.allSettled(promises);
    const failedResults = settledResults.filter((result: any) => {
      return (
        result?.value?.payload?.code === "ERR_NETWORK" ||
        (result?.value?.payload?.status >= responseTypes.internal_error &&
          result?.value?.payload?.status < responseTypes.other_error)
      );
    });

    if (failedResults.length) {
      loggedErrorForMandatoryApis(failedResults);
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  const loggedErrorForMandatoryApis = (failedResults: any) => {
    const pathWithQuery = window.location.pathname + window.location.search;
    const data: any = {
      route: pathWithQuery,
      hltId: loggedInUserInfo?.hltId,
      lobbyUserType: loggedInUserInfo?.lobbyUserType,
      displayName: loggedInUserInfo?.displayName,
      isImpersonated,
      date: Date(),
      browser: browserName,
      device: osName,
      impersonatedUserDetails: isImpersonated ? impersonationDetails : {},
      isConsultant: loggedInUserDetails.isConsultant,
      selectedInncode: userSelectedInnCode,
      reason: failedResults
    };
    dispatch(errorLogs(data));
  };

  useEffect(() => {
    if (isImpersonated) checkImpersonateMandatoryAPIs();
    else checkMandatoryAPIs();
  }, [isImpersonated, viewAs]);

  const checkImpersonateMandatoryAPIs = async () => {
    if (isImpersonated) {
      let promises: any = [];

      if (viewAs !== abovePropUserView["AP"] || lobbyUserType !== abovePropUserLobbyType) {
        promises.push(dispatch(fetchDefaultFilters({ types: ["other"] })));
      } else {
        dispatch(resetAllFilters());
      }

      promises.push(dispatch(fetchOnboardingQueriesCheck({ innCode: impersonationDetails?.userDetails?.innCodes })));
      handleApiFailure(promises);
    }
  };

  useEffect(() => {
    dispatch(fetchPrivacyAndDisclaimer());
    if (!isCorpUser)
      dispatch(fetchFilterPropertyInfo({ innCodesValue: localStorage.getItem("hltInnCodes"), isUserAssociated: true }));
  }, []);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={MUITheme}>
        <SessionTimeout />
        <Loader />
        <NavBar />
        <CssBaseline />
        {isImpersonated && <ImpersonateBanner />}
        {open ? (
          <div className="App">
            <Onboarding />
            {defaultFilter?.length === 0 && <CorpWizard />}
            <Box>
              <PreferredEmailDialog />
              <ManageEstateRoleDialog />
              <URLProvider>
                <Routes />
              </URLProvider>
            </Box>
          </div>
        ) : (
          <ErrorPage />
        )}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default Main;
